import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { refreshCustomers } from "./store";
import TxnDialog from "./TxnDialog";
import { SYNC_LABEL, customerStats, fmtTime, rp } from "./util";

function History({ c, txns, online, onClose, reload }) {
    const [server, setServer] = useState([]);
    const [open, setOpen] = useState(null);
    useEffect(() => {
        setServer([]);
        if (c && online) api.get("/pos/transactions", { params: { wa: c.wa }, timeout: 12000 })
            .then(({ data }) => setServer(data.map((t) => ({ ...t, customer_name: t.customer.name, customer_wa: t.customer.wa, sync_status: "SYNCED", _server: true })))).catch(() => {});
    }, [c, online]);
    const rows = useMemo(() => {
        if (!c) return [];
        const local = txns.filter((t) => t.customer_wa === c.wa);
        const ids = new Set(local.map((t) => t.id));
        return [...local, ...server.filter((t) => !ids.has(t.id))].sort((a, b) => b.created_at.localeCompare(a.created_at));
    }, [c, txns, server]);
    return (
        <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="pos-customer-dialog">
                <DialogHeader><DialogTitle className="font-serif text-xl">{c?.name}</DialogTitle></DialogHeader>
                <p className="text-xs text-neutral-500">WhatsApp {c?.wa}</p>
                <div className="max-h-[55vh] overflow-y-auto divide-y divide-mist border-y border-mist">
                    {rows.map((t) => (
                        <button key={t.id} onClick={() => setOpen(t.id)} className="w-full flex justify-between gap-3 py-2 text-left text-sm hover:bg-ivory" data-testid={`pos-customer-txn-${t.number}`}>
                            <div className="min-w-0">
                                <p className="font-mono text-xs">{t.number}</p>
                                <p className="text-[11px] text-neutral-500 truncate">{fmtTime(t.created_at)} · {t.event_name || ""} · {t.items.map((x) => `${x.qty}× ${x.sku}`).join(", ")}</p>
                            </div>
                            <div className="text-right shrink-0"><p>{rp(t.total)}</p><p className="text-[10px] text-neutral-500">{SYNC_LABEL[t.sync_status]}</p></div>
                        </button>
                    ))}
                    {!rows.length && <p className="py-4 text-sm text-neutral-400">No purchases on record.</p>}
                </div>
                <TxnDialog txn={rows.find((t) => t.id === open)} reload={reload} onClose={() => setOpen(null)} />
            </DialogContent>
        </Dialog>
    );
}

export default function Customers({ customers, txns, online, reload }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(null);
    useEffect(() => { if (online) refreshCustomers().then(reload).catch(() => {}); }, [online, reload]);
    const rows = useMemo(() => {
        const s = q.trim().toLowerCase();
        const d = s.replace(/\D/g, "");
        return customers.map((c) => ({ ...c, st: customerStats(c, txns) }))
            .filter((c) => !s || c.name.toLowerCase().includes(s) || (d && c.wa.includes(d.replace(/^0/, ""))))
            .sort((a, b) => (b.st.last || "").localeCompare(a.st.last || ""));
    }, [customers, txns, q]);
    return (
        <div className="space-y-3" data-testid="pos-customers">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or WhatsApp" data-testid="pos-customer-search"
                className="h-11 w-full rounded-md border border-mist bg-white px-3 outline-none focus:border-sage-dark" />
            <div className="overflow-x-auto rounded-md border border-mist bg-white">
                <table className="w-full min-w-[560px] text-sm">
                    <thead className="bg-ivory text-[10px] uppercase tracking-widest text-neutral-500">
                        <tr>{["Name", "WhatsApp", "Total Purchases", "Last Purchase"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-mist">
                        {rows.map((c) => (
                            <tr key={c.wa} onClick={() => setOpen(c)} className="cursor-pointer hover:bg-ivory" data-testid={`pos-customer-row-${c.wa}`}>
                                <td className="px-3 py-2">{c.name}</td>
                                <td className="px-3 py-2">{c.wa}</td>
                                <td className="px-3 py-2" data-testid={`pos-customer-count-${c.wa}`}>{c.st.count} · {rp(c.st.spent)}</td>
                                <td className="px-3 py-2">{fmtTime(c.st.last)}</td>
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={4} className="px-3 py-6 text-center text-neutral-400">No customers yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            <History c={open} txns={txns} online={online} reload={reload} onClose={() => setOpen(null)} />
        </div>
    );
}
